import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import { renameTemplate, clearRenameStatus } from "../slice";
import "./renameModal.css";

const RenameModal = ({
  show,
  onHide,
  templateId,
  currentName,
  onSuccess,
  onError,
}) => {
  const dispatch = useDispatch();

  const { renameTemplateStatus, renameTemplateError } = useSelector(
    (state) => state.templates
  );

  const [newName, setNewName] = useState("");
  const [isValid, setIsValid] = useState(false);
  const inputRef = useRef(null);

  // Initialize name when modal opens
  useEffect(() => {
    if (show && currentName) {
      setNewName(currentName);
      // Focus input after animation completes
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300);
    }
  }, [show, currentName]);

  // Validate name
  useEffect(() => {
    const trimmedName = newName.trim();
    setIsValid(trimmedName.length > 0 && trimmedName !== currentName);
  }, [newName, currentName]);

  const handleClose = useCallback(() => {
    setNewName("");
    setIsValid(false);
    dispatch(clearRenameStatus());
    onHide();
  }, [dispatch, onHide]);

  // Handle rename status changes
  useEffect(() => {
    if (renameTemplateStatus === "success") {
      onSuccess("Section Template renamed successfully.");
      handleClose();
      dispatch(clearRenameStatus());
    } else if (renameTemplateStatus === "failed") {
      onError(
        renameTemplateError || "Failed to rename template. Please try again."
      );
      dispatch(clearRenameStatus());
    }
  }, [
    renameTemplateStatus,
    renameTemplateError,
    onSuccess,
    onError,
    dispatch,
    handleClose,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid && templateId) {
      dispatch(renameTemplate({ templateId, name: newName.trim() }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isValid) {
      handleSubmit(e);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="rename-modal">
      <Modal.Body className="pt-0">
        <Modal.Header className="modal-title-with-border" closeButton>
          <Modal.Title>Rename Template</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-1 px-2">
          <div className="mb-4">
            <label className="form-label">Template Name</label>
            <input
              ref={inputRef}
              type="text"
              className="form-control"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter template name"
              maxLength={100}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={handleClose}
            disabled={renameTemplateStatus === "loading"}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid || renameTemplateStatus === "loading"}
          >
            {renameTemplateStatus === "loading" ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal.Body>
    </Modal>
  );
};

export default RenameModal;
